/**
 * Analytics tracking utilities for marketing pages
 * Integrates with Google Tag Manager, Reddit Pixel, and X Pixel
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    rdt?: (...args: any[]) => void;
    twq?: (...args: any[]) => void;
    lintrk?: (event: string, data?: any) => void;
    _linkedin_data_partner_ids?: string[];
  }
}

interface TrackingConfig {
  gtmId?: string;
  redditPixelId?: string;
  xPixelId?: string;
  linkedInPartnerId?: string;
}

export const tracking = {
  /**
   * Initialize tracking services with configuration
   * Called by TrackingScripts component after scripts load
   */
  init: (config: TrackingConfig) => {
    if (typeof window === 'undefined') return;

    // Reddit Pixel initialization
    if (config.redditPixelId && window.rdt) {
      window.rdt('init', config.redditPixelId, {
        optOut: false,
        useDecimalCurrencyValues: true,
      });
      console.log('[Tracking] Reddit Pixel initialized:', config.redditPixelId);
    }

    // X Pixel is already initialized in the script tag
    if (config.xPixelId && window.twq) {
      console.log('[Tracking] X Pixel initialized:', config.xPixelId);
    }

    // GTM is already initialized in the script tag
    if (config.gtmId && window.gtag) {
      console.log('[Tracking] Google Tag Manager initialized:', config.gtmId);
    }

    // LinkedIn Insight Tag is already initialized in the script tag
    if (config.linkedInPartnerId && window.lintrk) {
      console.log('[Tracking] LinkedIn Insight Tag initialized:', config.linkedInPartnerId);
    }
  },
  /**
   * Track page views across all analytics platforms
   */
  trackPageView: (path: string) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
      });
    }

    // Reddit Pixel
    if (window.rdt) {
      window.rdt('track', 'PageVisit');
    }

    // X (Twitter) Pixel
    if (window.twq) {
      window.twq('event', 'tw-page-view');
    }

    // LinkedIn Insight Tag
    if (window.lintrk) {
      window.lintrk('track', { conversion_id: 'page_view' });
    }

    console.log('[Tracking] Page view:', path);
  },

  /**
   * Track CTA button clicks for conversion optimization
   */
  trackCTAClick: (label: string, location: string) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'engagement',
        event_label: label,
        event_location: location,
      });
    }

    // Reddit Pixel
    if (window.rdt) {
      window.rdt('track', 'Custom', {
        customEventName: 'CTA_Click',
        label,
        location,
      });
    }

    // X (Twitter) Pixel
    if (window.twq) {
      window.twq('event', 'tw-cta-click', {
        label,
        location,
      });
    }

    console.log('[Tracking] CTA click:', { label, location });
  },

  /**
   * Track booking conversions (when user reaches Calendly step)
   */
  trackBookingInitiated: (eventType: string, value?: number) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager
    if (window.gtag) {
      window.gtag('event', 'booking_initiated', {
        event_category: 'conversion',
        event_label: eventType,
        value: value || 0,
      });
    }

    // Reddit Pixel
    if (window.rdt) {
      window.rdt('track', 'Lead', {
        value: value || 0,
        currency: 'USD',
      });
    }

    // X (Twitter) Pixel
    if (window.twq) {
      window.twq('event', 'tw-conversion', {
        conversion_id: 'booking_initiated',
        value: value || 0,
      });
    }

    // LinkedIn Insight Tag - Track conversion
    if (window.lintrk) {
      window.lintrk('track', { conversion_id: 'booking_initiated' });
    }

    console.log('[Tracking] Booking initiated:', { eventType, value });
  },

  /**
   * Track video plays (for demo videos)
   */
  trackVideoPlay: (videoTitle: string) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager
    if (window.gtag) {
      window.gtag('event', 'video_play', {
        event_category: 'engagement',
        event_label: videoTitle,
      });
    }

    console.log('[Tracking] Video play:', videoTitle);
  },

  /**
   * Track form submissions
   */
  trackFormSubmit: (formName: string) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager
    if (window.gtag) {
      window.gtag('event', 'form_submit', {
        event_category: 'engagement',
        event_label: formName,
      });
    }

    console.log('[Tracking] Form submit:', formName);
  },

  trackHeroSearch: (query: string) => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'hero_search', {
        event_category: 'engagement',
        search_query: query,
      });
    }
    console.log('[Tracking] Hero search:', query);
  },

  trackMomentPlay: (momentId: string, source: string) => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'moment_play', {
        event_category: 'engagement',
        moment_id: momentId,
        source,
      });
    }
    console.log('[Tracking] Moment play:', { momentId, source });
  },

  trackGuideCardClick: (guideType: 'ai' | 'human' | 'private') => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'guide_card_click', {
        event_category: 'engagement',
        guide_type: guideType,
      });
    }
    if (window.rdt) {
      window.rdt('track', 'Custom', {
        customEventName: 'Guide_Card_Click',
        guideType,
      });
    }
    console.log('[Tracking] Guide card click:', guideType);
  },

  trackOnboardingChoice: (choice: 'talk_to_guide' | 'book_guide' | 'import' | 'explore') => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'onboarding_choice', {
        event_category: 'conversion',
        choice,
      });
    }
    if (window.rdt) {
      window.rdt('track', 'SignUp', { choice });
    }
    console.log('[Tracking] Onboarding choice:', choice);
  },

  trackStartFreeClick: (source: string) => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'start_free_click', {
        event_category: 'conversion',
        source,
      });
    }
    if (window.rdt) {
      window.rdt('track', 'Lead', { source });
    }
    if (window.twq) {
      window.twq('event', 'tw-start-free', { source });
    }
    console.log('[Tracking] Start free click:', source);
  },

  trackPricingView: (source: string) => {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', 'pricing_view', {
        event_category: 'engagement',
        source,
      });
    }
    console.log('[Tracking] Pricing view:', source);
  },
};
